import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 12 — FeedbackRequest and versioned Feedback.
 * Mirrors fileQueries.js/practiceQueries.js's `request()` error contract
 * exactly (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const fbqk = {
  requests: (wrkId) => ["feedback-requests", wrkId],
  index: (wrkId, filter) => ["feedback", wrkId, filter || "all"],
  detail: (wrkId, fbkId) => ["feedback-detail", wrkId, fbkId],
};

export function useFeedbackRequests(workspaceId) {
  return useQuery({
    queryKey: fbqk.requests(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/feedback-requests`),
  });
}

export function useFeedbackEvidenceCatalog(workspaceId) {
  return useQuery({
    queryKey: ["feedback-evidence-catalog", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/evidence-catalog`),
  });
}

export function useFeedbackUnreadCount(workspaceId) {
  return useQuery({
    queryKey: ["feedback-unread-count", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/unread-count`),
    refetchInterval: 30000,
  });
}

export function useCreateFeedbackRequest(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback-requests`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: fbqk.requests(workspaceId) }),
  });
}

export function useCancelFeedbackRequest(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (frqId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback-requests/${frqId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: fbqk.requests(workspaceId) }),
  });
}

export function useFeedbackIndex(workspaceId, filter) {
  return useQuery({
    queryKey: fbqk.index(workspaceId, filter),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/feedback${filter && filter !== "all" ? `?filter=${filter}` : ""}`),
  });
}

export function useFeedbackDetail(workspaceId, fbkId) {
  return useQuery({
    queryKey: fbqk.detail(workspaceId, fbkId),
    enabled: !!workspaceId && !!fbkId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}`),
  });
}

function invalidateAllFeedback(qc, workspaceId) {
  qc.invalidateQueries({ queryKey: ["feedback", workspaceId] });
  qc.invalidateQueries({ queryKey: ["feedback-detail", workspaceId] });
  qc.invalidateQueries({ queryKey: fbqk.requests(workspaceId) });
}

export function useCreateFeedbackDraft(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback`, payload),
    onSuccess: () => invalidateAllFeedback(qc, workspaceId),
  });
}

export function useUpdateFeedbackDraft(workspaceId, fbkId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}`, { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: () => invalidateAllFeedback(qc, workspaceId),
  });
}

export function useDeleteFeedbackDraft(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fbkId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}`, { method: "DELETE" }),
    onSuccess: () => invalidateAllFeedback(qc, workspaceId),
  });
}

export function usePublishFeedback(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fbkId, audience_selection }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}/publish`, { audience_selection }),
    onSuccess: () => invalidateAllFeedback(qc, workspaceId),
  });
}

export function useAmendFeedback(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fbkId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}/amend`),
    onSuccess: () => invalidateAllFeedback(qc, workspaceId),
  });
}

export function useEvidenceRoomLaunch(workspaceId) {
  return useMutation({
    mutationFn: ({ fbkId, index }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}/evidence/${index}/room`),
  });
}

export function useRetryEvidence(workspaceId) {
  return useMutation({
    mutationFn: ({ fbkId, index }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}/evidence/${index}/retry`),
  });
}

export function useResponseRoomLaunch(workspaceId) {
  return useMutation({
    mutationFn: (fbkId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/feedback/${fbkId}/response/room`),
  });
}
